import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import SEO from './components/SEO';
import AppHelmetProvider from './components/HelmetProvider';
import { initializeCacheControl } from './cache-control';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from "@vercel/speed-insights/react";

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Contact = lazy(() => import('./pages/Contact'));
const Projects = lazy(() => import('./pages/Projects'));
const Courses = lazy(() => import('./pages/Courses'));
const Careers = lazy(() => import('./pages/Careers'));

// Lazy load legal pages
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const GDPRCompliance = lazy(() => import('./pages/legal/GDPRCompliance'));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const DeliveryPolicy = lazy(() => import('./pages/legal/DeliveryPolicy'));

// // Lazy load project pages
// const MasterServiceAgreement = lazy(() => import('./pages/project/MasterServiceAgreement'));
// const ServiceLevelAgreement = lazy(() => import('./pages/project/ServiceLevelAgreement'));
// const ProjectProposal = lazy(() => import('./pages/project/ProjectProposal'));
// const ProjectStatusReport = lazy(() => import('./pages/project/ProjectStatusReport'));
// const ChangeRequest = lazy(() => import('./pages/project/ChangeRequest'));
// const StatementOfWork = lazy(() => import('./pages/project/StatementOfWork'));
// const NonDisclosureAgreement = lazy(() => import('./pages/project/NonDisclosureAgreement'));

// // Lazy load client portal components
// const ClientLogin = lazy(() => import('./components/client/ClientLogin'));
// const ClientPortal = lazy(() => import('./components/client/ClientPortal'));

// Loading component
const LoadingSpinner = () => {
    const { isDarkMode } = useTheme();

    return (
        <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 ${isDarkMode
                ? 'border-primary-light'
                : 'border-primary'
                }`}></div>
        </div>
    );
};

// Error Boundary Component
const ErrorBoundary = ({ error }) => {
    const { isDarkMode } = useTheme();

    return (
        <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`p-8 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h1 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Oops! Something went wrong
                </h1>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {error?.message || 'An unexpected error occurred'}
                </p>
                <button
                    onClick={() => window.location.href = '/'}
                    className={`px-4 py-2 rounded-md ${isDarkMode
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-primary text-white hover:bg-primary/90'
                        }`}
                >
                    Return to Home
                </button>
            </div>
        </div>
    );
};

// 404 Not Found Component
const NotFound = () => {
    const { isDarkMode } = useTheme();

    return (
        <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`p-8 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h1 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    404 - Page Not Found
                </h1>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    The page you are looking for doesn't exist or has been moved.
                </p>
                <button
                    onClick={() => window.location.href = '/'}
                    className={`px-4 py-2 rounded-md ${isDarkMode
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-primary text-white hover:bg-primary/90'
                        }`}
                >
                    Return to Home
                </button>
            </div>
        </div>
    );
};

// Root Layout Component
const RootLayout = () => {
    return (
        <div className="flex flex-col min-h-screen">
            <SEO />
            <Navbar />
            <main className="flex-grow pt-16">
                <Suspense fallback={<LoadingSpinner />}>
                    <Outlet />
                </Suspense>
            </main>
            <Footer />
            <ScrollToTop />
        </div>
    );
};

function App() {
    useEffect(() => {
        // Initialize cache control
        initializeCacheControl();

        // Clear cache on component mount
        if (window.caches) {
            caches.keys().then(cacheNames => {
                cacheNames.forEach(cacheName => {
                    caches.delete(cacheName);
                });
            });
        }

        // Register service worker
        if ('serviceWorker' in navigator) {
            window.addEventListener('load', async () => {
                try {
                    const registration = await navigator.serviceWorker.register('/service-worker.js');
                    console.log('ServiceWorker registration successful:', registration);
                } catch (error) {
                    console.error('ServiceWorker registration failed:', error);
                }
            });
        }

        // Ensure theme is properly initialized
        const savedTheme = localStorage.getItem('theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const initialTheme = savedTheme === 'dark' || (!savedTheme && prefersDark);

        if (initialTheme) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, []);

    const router = createBrowserRouter([
        {
            path: "/",
            element: <RootLayout />,
            errorElement: <ErrorBoundary />,
            children: [
                {
                    index: true,
                    element: <Home />
                },
                {
                    path: "about",
                    element: <About />
                },
                {
                    path: "services",
                    element: <Services />
                },
                {
                    path: "projects",
                    element: <Projects />
                },
                {
                    path: "contact",
                    element: <Contact />
                },
                {
                    path: "courses",
                    element: <Courses />
                },
                {
                    path: "careers",
                    element: <Careers />
                },
                {
                    path: "legal",
                    children: [
                        {
                            path: "privacy-policy",
                            element: <PrivacyPolicy />
                        },
                        {
                            path: "terms-and-conditions",
                            element: <TermsAndConditions />
                        },
                        {
                            path: "cookie-policy",
                            element: <CookiePolicy />
                        },
                        {
                            path: "gdpr-compliance",
                            element: <GDPRCompliance />
                        },
                        {
                            path: "disclaimer",
                            element: <Disclaimer />
                        },
                        {
                            path: "refund-policy",
                            element: <RefundPolicy />
                        },
                        {
                            path: "delivery-policy",
                            element: <DeliveryPolicy />
                        }
                    ]
                },
                {
                    path: "*",
                    element: <NotFound />
                }
            ]
        }
    ], {
        future: {
            v7_startTransition: true,
            v7_relativeSplatPath: true
        }
    });

    return (
        <AppHelmetProvider>
            <ThemeProvider>
                <RouterProvider router={router} />
                <SpeedInsights />
                <Analytics />
            </ThemeProvider>
        </AppHelmetProvider>
    );
}

export default App;